import React from 'react';

/**
 * Inline glyphs for the UserMenu, drawn locally (same pattern as the
 * CountrySelector's own `SearchIcon`/`CloseGlyph`) rather than via the shared
 * `Icon` set — which is claim-gated and lacks the profile/translate/chevron
 * glyphs this popover needs. Every stroke is `currentColor`, so the styled
 * parent owns the colour (no literals here).
 */

/** Account / profile icon (popover trigger). */
export const UserGlyph = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M5 19c0-3.31 3.13-5.5 7-5.5s7 2.19 7 5.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Home icon — the SAME artwork the Owner Studio breadcrumb uses (a rounded house
 * with a doorway line). The studio renders it as a CDN-hosted mask; here it is
 * inlined as a `currentColor` stroke so it follows this popover's own colour and
 * matches the neighbouring `UserGlyph`. Used by the "back to Owner Studio" shortcut.
 */
export const HomeGlyph = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M2 12.2039C2 9.91549 2 8.77128 2.5192 7.82274C3.0384 6.87421 3.98695 6.28551 5.88403 5.10813L7.88403 3.86687C9.88939 2.62229 10.8921 2 12 2C13.1079 2 14.1106 2.62229 16.116 3.86687L18.116 5.10812C20.0131 6.28551 20.9616 6.87421 21.4808 7.82274C22 8.77128 22 9.91549 22 12.2039V13.725C22 17.6258 22 19.5763 20.8284 20.7881C19.6569 22 17.7712 22 14 22H10C6.22876 22 4.34315 22 3.17157 20.7881C2 19.5763 2 17.6258 2 13.725V12.2039Z"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M15 18H9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/** Translate icon (Language label). */
export const TranslateGlyph = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M3 6h8M7 6V4M9.5 6c0 3.4-2.2 6-6.5 7.2M5 9.2c0 2 1.8 3.7 4.2 4.6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.5 20.5l3.6-8.5h.3l3.6 8.5M14 17.4h4.6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Chevron-down (select affordance). */
export const ChevronGlyph = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Check mark (selected option). */
export const CheckGlyph = (): React.ReactElement => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
